
import * as Sentry from '@sentry/browser';
import { Integrations as TracingIntegrations } from '@sentry/tracing';

import { cdnURL, sentryDSN, sentryEnvironment } from 'env';

const pathRegex = {
  // Match paths with sign redirect - /s/{5 character guid}
  s: /\/s\/([a-zA-Z0-9]{8})/,
  // Match guids generically (40 char hexadecimal guids)
  generic: /\/([a-f0-9]{40})/,
};

/**
 * Configures Raven (Sentry client) with the appropriate user context.
 */
export default function configureRaven(userContext: Sentry.User | null) {

  if (!sentryEnvironment || !sentryDSN || !cdnURL) {
    return;
  }
  // Allow preproduction environments to avoid event sampling
  const tracesSampleRate = (sentryEnvironment === 'production') ? 0.006 : 1;
  const MAX_DUPLICATE_MESSAGES = 10;
  const messageCount: Record<string, number> = {
    "Can't execute code from a freed script": MAX_DUPLICATE_MESSAGES,
  };

  Sentry.init({
    dsn: sentryDSN,
    ignoreErrors: [
      'Non-Error promise rejection captured with value',
      '<unknown>',
      'Unauthorized',
      'Failed to fetch',
      'Request has been terminated',
      'An error occurred while saving P&S',
      'Oops! An error occurred when getting data. Please try again.',
    ],
    whitelistUrls: [cdnURL],
    sampleRate: 1,
    debug: sentryEnvironment === 'development',
    attachStacktrace: true,
    environment: sentryEnvironment,
    release: process.env.SENTRY_RELEASE,
    integrations: [
      new TracingIntegrations.BrowserTracing({
        // Replace guids with tokens so that common routescan be grouped together
        beforeNavigate: (context) => {
          let subGuid;
          /**
           * Replace guids {40 character hexadecimal strings} with :guid
           * examples:
           * /s/Sd12A08h => /s/:guid
           * /sign/b1358a4f976a43ec15bef7a5656b377726154421 => /sign/:guid
           * /prep-and-send/8f8f99a9a...43c1ce80e0f72/documents => /prep-and-send/:guid/documents
           */
          const name = Object.values(pathRegex).reduce((acc: string, val:RegExp) => {
            const match = new RegExp(val, 'g').exec(context.name);
            if (match && match.length >= 1) {
              subGuid = match[1];
              return context.name.replace(match[1], ':guid');
            }
            return acc;

          }, context.name);
          return {
            ...context,
            name,
            pathGuid: subGuid,
          };
        },
      }),
    ],
    tracesSampleRate,
    beforeSend(sentryEvent, hint) {
      let message;

      if (hint && hint.originalException) {
        if (typeof hint.originalException === 'string') {
          message = hint.originalException;
        } else {
          message = hint.originalException.message;
        }
      }

      // Block this user from sending more than ten of the
      // same error message in a single session.
      if (message) {
        messageCount[message] = (messageCount[message] || 0) + 1;
        if (messageCount[message] > MAX_DUPLICATE_MESSAGES) {
          return null;
        }
      }

      // Block all Sentry events that have `file:///` in
      // the URL. These are coming from users' personal
      // machines (presumably from the Microsoft
      // integration) and can very easily expose PII,
      // despite our safeguards on Sentry.
      if (sentryEvent.request && sentryEvent.request.url) {
        if (sentryEvent.request.url.indexOf('file:///') >= 0) {
          return null;
        }
      }

      return sentryEvent;
    },
  });

  Sentry.setUser(userContext);


  return Sentry;
}
